import { Link, useSearchParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { Assessment, BreadCrumb, Question, QuestionOption } from "../../../../types/dataModel";
import { useEffect, useState } from "react";
import DeleteModal from "../../../../core/modals/deleteModal";
import useFetch from "../../../../hooks/useFetch";
import PreLoader from "../../../../core/common/preLoader";
import { getAPIHeaders } from "../../../../common/Utils";
import axios from "axios";
import { API_URL } from "../../../../environment";
import { API } from "../../../../common/Constants";
import CopyRightInfo from "../../../../core/common/copyRightInfo";
import { useAccess } from "../../../../hooks/accessContext";
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import CustomToast from "../../../../core/common/customToast";
import AssessmentDownloadModal from "./assessmentDownload";
import AssessmentUploadModal from "./assessmentUpload";
import RichTextQuestionEditor from "./questionEditor/richTextQuestionEditor";

const breadCrums:BreadCrumb[] = [
  {
    title: "Performance",
    link : ""
  },
  {
    title: "Assessments",
    link : all_routes.assessments
  },
  {
    title: "Questions",
    link : ""
  },
]

const config = {
  loader: { load: ['input/tex', 'output/chtml'] },
};

function renderRowEndTag(opt: QuestionOption) {
  return (
      <div className="d-flex align-items-center justify-content-md-end flex-wrap row-gap-3">
        {
            opt.isCorrect
            ?
            <span className="badge badge-soft-success d-flex align-items-center me-3">
              <i className="fas fa-circle fs-6 me-1" />
              Correct
            </span>
            :
            <span className="badge badge-soft-danger d-flex align-items-center me-3">
              <i className="fas fa-circle fs-6 me-1" />
              Incorrect
            </span>
        }

      </div>
  )
}

const RichTextAssessmentDetails = () => {
  const { hasAcademicMgntEdit, hasPerformanceMgntCreate} = useAccess()
  const [searchParams] = useSearchParams();
  const {isLoading, error, callFetch } = useFetch();
  const [assessment, setAssessment] = useState<Assessment>();
  const [deleteItem, setDeleteItem] = useState<any>({
    question: undefined,
    message: ""
  })
  const [editQuestion, setEditQuestion] = useState<Question>();
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [questionErrorMessage, setQuestionErrorMessage] = useState<string>("")
  const [showQuestionsList, setShowQuestionsList] = useState<boolean>(true);
  const [questionFilter, setQuestionFilter] = useState<"all" | "ai" | "manual">("all");
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
      show: false,
      message: "Something went wrong please try again later",
      heading: "Oops! Something went wrong",
      type: "ERROR",
  });
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [dragSourceIndex, setDragSourceIndex] = useState<number | null>(null);
  const [activeQuestionIdx, setActiveQuestionIdx] = useState<number | null>(null);
  const [downloadFormData, setDownloadFormData] = useState({
    generateOMRAnswerSheet: true,
    generateAnswerKey: false,
    includeExamDateAndTime: true,
    examDate: "",
    examStartTime: "",
    examDuration: "",
    marksPerQuestion: "",
    negativeMarksPerQuestion: "",
    includeInstitutionDetails: true,
    includeAddress: true,
    includeContactDetails: false,
    generalInstructions: "",
    markingSchema: "",
    importantNotes: "",
    candidateInstructions: ""
  });

  useEffect(() => {
    if (error) {
      setToastSettings({
        message: error?.response?.data || 'Something went wrong please try again later.',
        heading: "Ooops",
        show: true,
        type: "ERROR"
      })
      setTimeout(() => {
        setErrorMessage("");
        if(assessment?.id){
          fetchAssessment(assessment?.id)
        }
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    setInProgress(isLoading)
  },[isLoading])

  const renderCrums = (crums:any) => {
    return (
      <nav>
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to={all_routes.adminDashboard}>
              <i className="ti ti-smart-home" />
            </Link>
          </li>
          {crums?.map((item:any, idx:number) => {
            if (crums?.length - 1 === idx) {
              return (
              <li key={idx} className="breadcrumb-item active" aria-current="page">
                {item.title}
              </li>)
            } else if (!item?.link) {
              return (<>
                <li key={idx}  className="breadcrumb-item">{item.title}</li>
              </>)
            } else {
              return (
                <li key={idx}  className="breadcrumb-item">
                  <Link to={item?.link}>
                    {item.title}
                  </Link>
                </li>
              )
            }
          })}
        </ol>
      </nav>
    )
  }

  useEffect(() => {
    let id = searchParams.get("id")
    if (id){
      fetchAssessment(parseInt(id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const validateQuestion = (question: Question): boolean => {
    // Reset error message before validation
    setQuestionErrorMessage("")
    
    // 1. Check if question text is empty
    if (!question.questionText || question.questionText.trim() === "") {
      setQuestionErrorMessage("Question text cannot be empty.")
      return false
    }
    
    // 2. Check if question is duplicate
    const existingQuestions = assessment?.questions || []
    const isDuplicate = existingQuestions.some((q, index) => {
      // Skip comparing with itself when editing
      if (question.index !== undefined && question.index === index) {
        return false
      }
      return q.questionText?.trim().toLowerCase() === question.questionText?.trim().toLowerCase()
    })
    
    if (isDuplicate) {
      setQuestionErrorMessage("This question already exists in the assessment.")
      return false
    }
    
    // 3. Check if there are enough options
    if (!question.options || question.options.length < 2) {
      setQuestionErrorMessage("Question must have at least 2 options.")
      return false
    }
    
    // 4. Check for empty options
    const hasEmptyOption = question.options.some(opt => !opt.optionText || opt.optionText.trim() === "")
    if (hasEmptyOption) {
      setQuestionErrorMessage("Option text cannot be empty.")
      return false
    }
    
    // 5. Check for duplicate options
    const optionTexts = question.options.map(opt => opt.optionText?.trim().toLowerCase() || '')
    const uniqueOptions = new Set(optionTexts)
    if (uniqueOptions.size !== optionTexts.length) {
      setQuestionErrorMessage("Duplicate options are not allowed.")
      return false
    }
    
    // 6. Check if at least one option is marked as correct
    const hasCorrectOption = question.options.some(opt => opt.isCorrect === true)
    if (!hasCorrectOption) {
      setQuestionErrorMessage("At least one option must be marked as correct.")
      return false
    }
    
    // 7. Check if at least one option is marked as incorrect
    const hasIncorrectOption = question.options.some(opt => opt.isCorrect === false)
    if (!hasIncorrectOption) {
      setQuestionErrorMessage("At least one option must be marked as incorrect.")
      return false
    }
    
    return true
  }

  const onSave = (question:Question) => {
    if (!validateQuestion(question)) {
      return
    }

    if(!question?.id){
      addQuestion(question)
    } else if (question?.id !== undefined && question?.id >= 0){
      updateQuestion(question)
    }
  }

  const updateQuestion = (question:Question) => {
    callFetch(API.ASSESSMENT,
      'PUT',
      getAPIHeaders(),
      null,
      {
         ...assessment,
         questions: [question]
      }
    ).then(()=>{
      fetchAssessment(assessment?.id || 0)
      setToastSettings({
        message: "Question updated successfully!",
        heading: "Success",
        show: true,
        type: "SUCCESS"
      })
      setShowQuestionsList(true)
      setEditQuestion(undefined)
      setQuestionErrorMessage("")
    }).catch(()=>{
    })
  }

  const addQuestion = (question:Question) => {
    callFetch(API.ASSESSMENT_QUESTION.replace(':assessmentId', assessment?.id?.toString() || ''),
      'PUT',
      getAPIHeaders(),
      null,
      [{
        ...question, 
        displayOrder: (assessment?.questions?.length || 0) + 1 
      }]
    ).then(()=>{
      fetchAssessment(assessment?.id || 0)
      setToastSettings({
        message: "Question added successfully!",
        heading: "Success",
        show: true,
        type: "SUCCESS"
      })
      setShowQuestionsList(true)
      setEditQuestion(undefined)
      setQuestionErrorMessage("")
    }).catch(()=>{
    })
  }

  const fetchAssessment = (id:number) => {
        callFetch(`${API.ASSESSMENT}/${id}`,
          'GET',
          getAPIHeaders(),
          null,
          null
      ).then((res)=>{
        if(res){
          setAssessment(res)
        }
      });
  }

  const handleDownloadAnswerKeys = () => {
    if (assessment?.id) {
      setInProgress(true)
      const headers = { ...getAPIHeaders() };
      delete (headers as any)['Content-Type']; // Remove Content-Type for binary data
      
      axios.post(`${API_URL}/${API.ASSESSMENT_GEN_ANS_KEY.replace(':assessmentId', assessment.id.toString())}`, null, {
        headers: headers,
        responseType: 'blob'
      }).then((response) => {
        if (response.data) {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `answer-key-${assessment.title || assessment.id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      }).catch(() => {}).finally(() => {
        setInProgress(false)
      });
    }
  };

  const handleDownloadPdf = () => {
    setShowDownloadModal(true);
  };

  const handleDownloadAssessment = () => {
    if (assessment?.id) {
      setInProgress(true)
      const headers = { ...getAPIHeaders() };
      delete (headers as any)['Content-Type']; // Remove Content-Type for binary data
      
      axios.post(`${API_URL}/${API.ASSESSMENT_EXPORT.replace(':assessmentId', assessment.id.toString())}`, downloadFormData, {
        headers: headers,
        responseType: 'blob'
      }).then((response) => {
        if (response.data) {
          // Create a blob from the response and trigger download
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `assessment-${assessment.title || assessment.id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          setShowDownloadModal(false);
          setToastSettings({
            message: "Assessment downloaded successfully!",
            heading: "Success",
            show: true,
            type: "SUCCESS"
          });
        }
      }).catch(() => {
        setToastSettings({
          message: "Failed to download assessment. Please try again.",
          heading: "Download Failed",
          show: true,
          type: "ERROR"
        });
      }).finally(() => {
        setInProgress(false)
      });;
    }
  };
  
  const handleUploadQuestionsClick = () => {
    setShowUploadModal(true);
  };

  const deleteQuestion = () => {
    callFetch(API.ASSESSMENT_QUESTION.replace(':assessmentId', assessment?.id?.toString() || ''),
      'DELETE',
      getAPIHeaders(),
      null,
      [
        deleteItem?.question?.id
      ]
    ).then(()=>{
      fetchAssessment(assessment?.id || 0)
      setToastSettings({
        message: "Question deleted successfully!",
        heading: "Success",
        show: true,
        type: "SUCCESS"
      })
    })
  }

  const handleDragStart = (startIndex: number) => {
    setDragSourceIndex(startIndex);
    setActiveQuestionIdx(startIndex);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (dropIndex: number, filteredQuestions: Question[]) => {
    if (dragSourceIndex === null) return;

    // Compute mapping between filtered list indices and real indices in full questions
    const fullList = [...(assessment?.questions || [])];

    const sourceQuestion = filteredQuestions[dragSourceIndex];
    const targetQuestion = filteredQuestions[dropIndex];
    if (!sourceQuestion || !targetQuestion) {
      setDragSourceIndex(null);
      setActiveQuestionIdx(null);
      return;
    }

    const sourceFullIndex = fullList.findIndex(q => q.id === sourceQuestion.id);
    const targetFullIndex = fullList.findIndex(q => q.id === targetQuestion.id);
    if (sourceFullIndex === -1 || targetFullIndex === -1) {
      setDragSourceIndex(null);
      setActiveQuestionIdx(null);
      return;
    }

    // Reorder in the full list
    const updated = [...fullList];
    const [moved] = updated.splice(sourceFullIndex, 1);
    updated.splice(targetFullIndex, 0, moved);

    const updatedWithOrder = updated.map((q, idx) => ({ ...q, displayOrder: idx + 1 }));
    callFetch(
      API.ASSESSMENT,
      'PUT',
      getAPIHeaders(),
      null,
      {
        id: assessment?.id,
        title: assessment?.title,
        description: assessment?.description,
        code: assessment?.code,
        questions: updatedWithOrder
      }
    ).then(() => {
      setAssessment({
        ...assessment,
        questions: updatedWithOrder
      });
      setActiveQuestionIdx(dropIndex);
    }).catch(() => {}).finally(() => {
      setDragSourceIndex(null);
    });
  };

  const renderTitleAndStats = (academicProps: Question[]) => {
    // Count AI and manual questions
    const aiQuestions = academicProps.filter(q => q.isAiGenerated).length;
    const manualQuestions = academicProps.length - aiQuestions;
    
    return (
      <div className="row gy-3 mb-3">
        <div className="col-sm-4">
          <div className="d-flex align-items-center">
            <h4>Questions</h4>
            <span className="badge badge-dark rounded-pill badge-xs ms-2">
              {assessment?.questions?.length}
            </span>
          </div>
        </div>
        <div className="col-sm-8">
          <div className="d-flex justify-content-end align-items-center">
            <div className="d-md-flex align-items-center me-3 p-2">
                <button type="button" className="btn btn-sm btn-outline-primary me-2 mb-2" onClick={handleDownloadAnswerKeys}>
                  <i className="ti ti-download me-1" /> Answer Keys
                </button>
                <button type="button" className="btn btn-sm btn-outline-primary me-2 mb-2" onClick={handleDownloadPdf}>
                  <i className="ti ti-file-type-pdf me-1" /> PDF
                </button>
                <button type="button" className="btn btn-sm btn-outline-primary me-2 mb-2" onClick={handleUploadQuestionsClick} disabled={!hasPerformanceMgntCreate()}>
                  <i className="ti ti-upload me-1" /> Upload
                </button>
                <div className="btn-group mb-2 p-0" role="group">
                     <button 
                       type="button"
                       className={`btn btn-sm compact-btn ${questionFilter === "all" ? "btn-primary" : "btn-outline-primary"}`}
                       onClick={() => setQuestionFilter("all")}
                     >
                       All ({academicProps.length})
                     </button>
                     <button 
                       type="button" 
                       className={`btn btn-sm compact-btn ${questionFilter === "ai" ? "btn-primary" : "btn-outline-primary"}`}
                       onClick={() => setQuestionFilter("ai")}
                     >
                       <i className="ti ti-robot me-1"></i>AI Generated ({aiQuestions})
                     </button>
                     <button 
                       type="button" 
                       className={`btn btn-sm compact-btn ${questionFilter === "manual" ? "btn-primary" : "btn-outline-primary"}`}
                       onClick={() => setQuestionFilter("manual")}
                     >
                       <i className="ti ti-user me-1"></i>Manually Created ({manualQuestions})
                     </button>
               </div>
            </div>

          </div>
        </div>
      </div>
    )
  }

  const renderAccordingTabHeader = (academicProps: Question[]) => {

    return (
      <div className="row border-bottom mb-3">

        {
          assessment?.isLive
          ?
          <></>
          :
          <>
            <div className="col-lg-12">
              <div className="d-contents align-items-center justify-content-lg-between flex-wrap row-gap-3 mb-3">
                <div className="d-flex align-items-center">
                      {showQuestionsList ? (
                        <button
                          disabled={!hasPerformanceMgntCreate()}
                          className="btn bg-primary-transparent border-dashed border-primary w-100 text-start"
                          onClick={()=>{
                            setEditQuestion({
                              index: undefined,
                              questionText:"",
                              options:[]
                            })
                            setShowQuestionsList(false)
                          }}
                        >
                          <i className="ti ti-plus me-2" />
                          Add New Question
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-primary w-100 text-start"
                          onClick={()=>{
                            setEditQuestion(undefined)
                            setShowQuestionsList(true)
                            setQuestionErrorMessage("")
                          }}
                        >
                          <i className="ti ti-list me-2" />
                          Show Questions
                        </button>
                      )}
                </div>
              </div>
            </div>
          </>
        }

        {
          errorMessage
          ?
          <div className="col-lg-12 mb-1">
            <div className="br-5 text-white d-flex align-items-center justify-content-center gap-1 bg-danger">
              <i className="fs-16 ti ti-alert-triangle"/> 
              {errorMessage}
            </div>
          </div>
          :
          <></>
        }

      </div>
    )
  }

  const renderMathJaxOption = (opt: QuestionOption, index:number) => {
    return (
      <div key={index} className="accordion-body row align-items-center row-gap-3 shadow-sm pt-2 pb-2">
        <div className="col-lg-11 col-md-11">
          <div className="todo-inbox-check d-flex align-items-center flex-wrap row-gap-3">
            <div className="strike-info d-flex justify-center">
              <h4 className="fs-14 me-2">
                <span className="text-dark">{index+1}.</span>
              </h4>
              <MathJaxContext config={config}>
                  <MathJax dynamic>{opt.optionText}</MathJax>
              </MathJaxContext>
            </div>
          </div>
        </div>
        <div className="col-lg-1 col-md-1">
            {renderRowEndTag(opt)}
        </div>
      </div>
    )
  }

  const renderRichTextOption = (opt: QuestionOption, index:number) => {
    return (
      <div key={index} className="accordion-body row align-items-center row-gap-3 shadow-sm pt-2 pb-2">
        <div className="col-lg-11 col-md-11">
          <div className="todo-inbox-check d-flex align-items-center flex-wrap row-gap-3">
            <div className="strike-info d-flex justify-center">
              <h4 className="fs-14 me-2">
                <span className="text-dark">{index+1}.</span>
              </h4>
              {/* <MathJaxContext config={config}>
                  <MathJax dynamic>{opt.optionText}</MathJax>
              </MathJaxContext> */}
              <div
                className="rich-text-content"
                dangerouslySetInnerHTML={{ __html: opt.optionText || '' }}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-1 col-md-1">
            {renderRowEndTag(opt)}
        </div>
      </div>
    )
  }

  const renderRichTextQuestion = (questions: Question[], filteredQuestions: Question[], question: Question, index:number) => {
    return (
      <div
        className={`accordion-item ${activeQuestionIdx === index ? 'border border-primary rounded' : ''}`}
        key={question.id || index}
        draggable={hasAcademicMgntEdit() && !assessment?.isLive}
        onDragStart={() => handleDragStart(index)}
        onDragOver={handleDragOver}
        onDrop={() => handleDrop(index, filteredQuestions)}
      >
        
          <h2 className="accordion-header flex-row d-flex" id={`question-heading${index}`}>
            {
              assessment?.isLive || !hasAcademicMgntEdit()
              ?
              <></>
              :
              <>
                <div className="flex-column d-flex justify-content-evenly bg-soft-secondary">
                          <button className="btn btn-secondary p-0 m-2"
                          onClick={()=>{
                            setEditQuestion({
                              ...question,
                              index: questions.indexOf(question),
                              displayOrder: questions.indexOf(question) + 1
                            })
                            setShowQuestionsList(false)
                          }}>
                            <i className="ti ti-edit"/>
                          </button>
                          <button 
                            className="btn btn-danger p-0 mb-2 ms-2 me-2"
                            data-bs-toggle="modal" data-inert={true}
                            data-bs-target="#delete_modal"
                            onClick={()=>{
                              setDeleteItem({
                                question: question,
                                message: `Are you sure you want to delete question #${questions.indexOf(question) + 1}?`
                              })
                            }}
                            >
                            <i className="ti ti-trash"/>
                          </button>
                </div>
              </>
            }
            <button className="accordion-button" 
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#question-collapse${index}`}
              aria-expanded="true"
              aria-controls={`question-collapse${index}`}
              onClick={() => setActiveQuestionIdx(prev => prev === index ? null : index)}>

                <div className="d-flex align-items-center w-100">
                    <span className="text-dark me-2">#Q{question.displayOrder || questions.indexOf(question)+1}. </span>
                    {/* Render stored HTML from the rich text editor */}
                    <div
                      className="rich-text-content"
                      dangerouslySetInnerHTML={{ __html: question.questionText || '' }}
                    />
                    <div className="ms-auto d-flex align-items-center">
                      {question.isAiGenerated && 
                        <span className="badge badge-soft-primary me-3">
                          <i className="ti ti-robot me-1"></i>AI Generated
                        </span>
                      }
                      {hasAcademicMgntEdit() && !assessment?.isLive && (
                        <i className="ti ti-grip-vertical text-muted me-2" style={{ cursor: 'grab' }} title="Drag to reorder"></i>
                      )}
                    </div>
                </div>     
            </button>
          </h2>
        <div id={`question-collapse${index}`}
          className="accordion-collapse collapse show"
          aria-labelledby={`question-heading${index}`}>
          <div className="accordion-body">
            {question?.options?.map((option:QuestionOption,optIndex:number)=>(
                renderRichTextOption(option,optIndex)                   
            ))}
           </div> 
        </div>
      </div>
    )
  }

  const renderMathJaxQuestion = (questions: Question[], filteredQuestions: Question[], question: Question, index: number) => {
    return (
      <div
        className={`accordion-item ${activeQuestionIdx === index ? 'border border-primary rounded' : ''}`}
        key={question.id || index}
        draggable={hasAcademicMgntEdit() && !assessment?.isLive}
        onDragStart={() => handleDragStart(index)}
        onDragOver={handleDragOver}
        onDrop={() => handleDrop(index, filteredQuestions)}
      >

        <h2 className="accordion-header flex-row d-flex" id={`question-heading${index}`}>
          {
            assessment?.isLive || !hasAcademicMgntEdit()
              ?
              <></>
              :
              <>
                <div className="flex-column d-flex justify-content-evenly bg-soft-secondary">
                  <button className="btn btn-secondary p-0 m-2"
                    onClick={() => {
                      setEditQuestion({
                        ...question,
                        index: questions.indexOf(question) // Use the original index in the full array
                      })
                      setShowQuestionsList(false)
                    }}>
                    <i className="ti ti-edit" />
                  </button>
                  <button
                    className="btn btn-danger p-0 mb-2 ms-2 me-2"
                    data-bs-toggle="modal" data-inert={true}
                    data-bs-target="#delete_modal"
                    onClick={() => {
                      setDeleteItem({
                        question: question,
                        message: `Are you sure you want to delete question #${questions.indexOf(question) + 1}?`
                      })
                    }}
                  >
                    <i className="ti ti-trash" />
                  </button>
                </div>
              </>
          }
          <button className="accordion-button"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target={`#question-collapse${index}`}
            aria-expanded="true"
            aria-controls={`question-collapse${index}`}
            onClick={() => setActiveQuestionIdx(prev => prev === index ? null : index)}>

            <div className="d-flex align-items-center w-100">
              <span className="text-dark me-2">#Q{questions.indexOf(question) + 1}. </span>
              <MathJaxContext config={config}>
                <MathJax dynamic>{question.questionText}</MathJax>
              </MathJaxContext>
              <div className="ms-auto d-flex align-items-center">
                {question.isAiGenerated &&
                  <span className="badge badge-soft-primary me-3">
                    <i className="ti ti-robot me-1"></i>AI Generated
                  </span>
                }
                {hasAcademicMgntEdit() && !assessment?.isLive && (
                  <i className="ti ti-grip-vertical text-muted me-2" style={{ cursor: 'grab' }} title="Drag to reorder"></i>
                )}
              </div>
            </div>
          </button>
        </h2>
        <div id={`question-collapse${index}`}
          className="accordion-collapse collapse show"
          aria-labelledby={`question-heading${index}`}>
          <div className="accordion-body">
            {question?.options?.map((option: QuestionOption, optIndex: number) => (
              renderMathJaxOption(option, optIndex)
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderAccordingTabsBody = (questions : Question[]) => {
    // Filter questions based on selected filter
    const filteredQuestions = questions.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)).filter((question:Question)   => {
      if (questionFilter === "all") return true;
      if (questionFilter === "ai") return question.isAiGenerated === true;
      if (questionFilter === "manual") return question.isAiGenerated !== true;
      return true;
    });
    
    if (filteredQuestions.length === 0) {
      return (
        <div className="alert alert-info mt-3">
          <i className="ti ti-info-circle me-2"></i>
          No questions match the selected filter.
        </div>
      );
    }
    
    return (
      <div className="accordion" id="accordionquestionExample">
        {
          filteredQuestions?.map((question:Question, index:number)=>{
            // if(question.metadata?.editorType === 'richText'){
            //   return renderRichTextQuestion(questions, filteredQuestions, question, index)
            // } else {
            //   return renderMathJaxQuestion(questions, filteredQuestions, question, index)
            // }
            return renderRichTextQuestion(questions, filteredQuestions, question, index)
          })
        }
      </div>
    )
  }

  return (
    <>
      <>
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
            setToastSettings((prev) => ({
                ...prev,
                show: false,
            }));
        }}
      />
      <PreLoader showLoader={inProgress} />
      <DeleteModal 
            heading="Confirm Delete"
            message={deleteItem.message}
            onDelete={()=>deleteQuestion()}
          />
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            {/* Breadcrumb */}
            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
              <div className="my-auto mb-2">
                <h2 className="mb-1">{assessment?.title}
                <span className="text-gray fs-18 ms-2 mb-1">
                 (Code: {assessment?.code})
                </span>
                </h2>
                {renderCrums(breadCrums)}
              </div>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                <div className="ms-2 mb-0 head-icons">
                  <CollapseHeader />
                </div>
              </div>
            </div>
            {showQuestionsList ? (
              <div className="card">
                <div className="card-body">
                  {renderTitleAndStats(assessment?.questions || [])}
                  {renderAccordingTabHeader(assessment?.questions || [])}
                  {renderAccordingTabsBody(assessment?.questions || [])}
                </div>
              </div>
            ) : (
              <>
                {renderAccordingTabHeader(assessment?.questions || [])}
                <RichTextQuestionEditor
                  question={editQuestion}
                  onSave={onSave}
                  onCancel={() => {
                    setEditQuestion(undefined)
                    setShowQuestionsList(true)
                    setQuestionErrorMessage("")
                  }}
                  errorMessage={questionErrorMessage}
                />
              </>
            )}
          </div>
          <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
            <CopyRightInfo/>
          </div>
        </div>
        {/* /Page Wrapper */}
      </>

      {/* Upload input will be attached when API details are provided */}

      {/* <QuestionModal 
        question={editQuestion}
        heading="Add/Edit Question" 
        errorMessage={questionErrorMessage}
        onSave={onSave}
        onCancel={()=>{
          setEditQuestion(undefined)
          setShowQuestions(false)
          setQuestionErrorMessage("")
        }}
        show={showQuestions}
      /> */}

      <AssessmentDownloadModal
        show={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadAssessment}
        downloadFormData={downloadFormData}
        setDownloadFormData={setDownloadFormData}
        assessmentId={assessment?.id?.toString() || ''}
      />

      <AssessmentUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        assessmentId={assessment?.id?.toString() || ''}
        onSuccess={() => {
          // Refresh the assessment data after successful upload
          if (assessment?.id) {
            fetchAssessment(assessment.id);
          }
        }}
      />

      {/* <ScientificEditorModal 
        question={editQuestion}
        heading="Add/Edit Scientific Question" 
        errorMessage={questionErrorMessage}
        onSave={onSave}
        onCancel={()=>{
          setEditQuestion(undefined)
          setShowQuestions(false)
          setQuestionErrorMessage("")
        }}
        show={showQuestions}
      /> */}
    </>
  );
};


export default RichTextAssessmentDetails;
